'use client';

import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { Colors } from './types';
import { cn } from './lib/utils';
import { useFlatUI } from './flat-ui';
import { useRipple } from './effects/ripple';

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
    mode?: 'toggle' | 'onOff';
    color?: 'default' | Colors;
  }
>(({ className, mode = 'onOff', color = 'default', ...props }, ref) => {
  const flat = useFlatUI();
  const { rippleHandlers, rippleClassName } = useRipple({
    variant: 'ghost',
  });

  return (
    <SwitchPrimitives.Root
      className={cn(
        'peer inline-flex my-1 ml-2 mr-2 h-3 w-8 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
        flat && 'shadow-none',
        {
          'data-[state=unchecked]:bg-popover-foreground/20': mode === 'onOff',
        },
        mode === 'toggle' && {
          'data-[state=unchecked]:bg-popover-foreground/20':
            color === 'default',
          'data-[state=unchecked]:bg-primary/70': color === 'primary',
          'data-[state=unchecked]:bg-secondary/70': color === 'secondary',
          'data-[state=unchecked]:bg-success/70': color === 'success',
          'data-[state=unchecked]:bg-info/70': color === 'info',
          'data-[state=unchecked]:bg-error/70': color === 'error',
          'data-[state=unchecked]:bg-warning/70': color === 'warning',
        },
        {
          'data-[state=checked]:bg-popover-foreground/40': color === 'default',
          'data-[state=checked]:bg-primary/70': color === 'primary',
          'data-[state=checked]:bg-secondary/70': color === 'secondary',
          'data-[state=checked]:bg-success/70': color === 'success',
          'data-[state=checked]:bg-info/70': color === 'info',
          'data-[state=checked]:bg-error/70': color === 'error',
          'data-[state=checked]:bg-warning/70': color === 'warning',
        },

        className
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          'block h-5 w-5 rounded-full bg-primary transition-all duration-200 ease-in-out shadow-[0_0_4px_1px_rgba(0,0,0,0.2)] hover:shadow-[0_0_6px_1px_rgba(0,0,0,0.15)] ring-0 hover:scale-105 data-[state=checked]:translate-x-3 data-[state=unchecked]:-translate-x-1 active:scale-95 active:shadow-[0_0_4px_1px_rgba(0,0,0,0.25)]',
          flat && 'shadow-none hover:shadow-none active:shadow-none',
          // Colour the knob per state, not per variant. Keying it on `color`
          // alone left every coloured switch showing a full-strength knob while
          // the track was still grey, which reads as half-on rather than off.
          {
            'bg-popover': color === 'default',
            'data-[state=unchecked]:bg-popover-foreground/40': color !== 'default',
            'data-[state=checked]:bg-primary': color === 'primary',
            'data-[state=checked]:bg-secondary': color === 'secondary',
            'data-[state=checked]:bg-success': color === 'success',
            'data-[state=checked]:bg-info': color === 'info',
            'data-[state=checked]:bg-error': color === 'error',
            'data-[state=checked]:bg-warning': color === 'warning',
          },
          rippleClassName
        )}
        {...rippleHandlers}
      />
    </SwitchPrimitives.Root>
  );
});
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
